import React, { useState } from 'react'
import { App, Alert, Button, DatePicker, Descriptions, Input, InputNumber, Space, Switch, Table, Tag, Typography } from 'antd'
import type { Dayjs } from 'dayjs'
import { supabaseClient } from '../../lib/supabaseClient'
import { SectionCard } from '../../theme/listkit'
import { edgeErrorMessage } from '../../lib/edgeError'

/** Shape `feed-purge` returns on a 2xx. */
export interface PurgeResult {
  ok: boolean
  dry_run: boolean
  hard_delete: boolean
  from: string
  to: string
  matched: number
  deleted: number
  sample?: Array<{ id: string; text?: string; created_at?: string | null }>
  /** Present ONLY on a partial failure — and it still arrives with HTTP 200. */
  failures?: Array<{ chunk: number; error: string }>
  note?: string
}

/** The server truncates the dry-run sample to this many rows (feed-purge/index.ts). */
export const SAMPLE_CAP = 5

/**
 * How long a preview stays actionable.
 *
 * A preview is evidence about a moment. The server does not delete "the items
 * you reviewed" — it re-runs the query (feed-purge/index.ts re-calls
 * queryActivities with only the filter, never an id list), so the longer a
 * preview sits, the more the live sweep can diverge from it.
 */
export const PREVIEW_STALE_MS = 10 * 60 * 1000

/**
 * The confirmation phrase the server demands for a live run.
 *
 * MUST be derived from the exact same `from`/`to` strings the request carries —
 * the server rebuilds it from the body it received (`_shared/feedPurgePlan.ts`
 * `confirmPhrase`), so a phrase built from a re-formatted or re-parsed date
 * would not match. We mirror that by holding the sent strings in state and
 * never re-deriving them.
 *
 * The HARD prefix mirrors _shared/feedPurgePlan.ts exactly: a phrase typed for a
 * reversible purge must not authorise an irreversible one. Keep the two in step
 * — if they drift, every hard purge fails closed with a 428.
 */
export function confirmPhraseFor(from: string, to: string, hardDelete = false): string {
  return `${hardDelete ? 'HARD ' : ''}${from}..${to}`
}

/**
 * Freeze the upper bound of the window at the moment of preview.
 *
 * THIS IS THE LOAD-BEARING SAFETY FIX, so it is a pure function with its own
 * tests. The natural operator input for "clear everything up to now" is a range
 * ending at or after the present moment. Because the live run RE-QUERIES rather
 * than deleting the reviewed ids, an open-ended upper bound means every post
 * members make between the preview and the confirmation falls inside the blast
 * radius — the operator approves 12 items and destroys 47.
 *
 * Clamping `to` to the preview instant closes that: activities created after the
 * preview carry a later `created_at` and fall outside the half-open [from, to)
 * filter, so they cannot be swept by a confirmation made earlier. The live run
 * then reuses this exact frozen string rather than recomputing from the picker.
 */
export function freezeUpperBound(selectedTo: Date, now: Date): Date {
  return selectedTo.getTime() > now.getTime() ? now : selectedTo
}

/**
 * Bulk purge by date range — the admin counterpart to the `feed-purge` Edge
 * Function.
 *
 * Stream has no delete-by-date-range and its console's only bulk control wipes
 * an ENTIRE feed group, so this is the only way to remove a bounded window (say,
 * the pre-launch test posts) while keeping everything since.
 */
export function BulkPurgeCard({ onPurged }: { onPurged?: () => void }) {
  const { message } = App.useApp()
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [hardDelete, setHardDelete] = useState(false)
  const [max, setMax] = useState<number>(500)
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState<PurgeResult | null>(null)
  const [confirm, setConfirm] = useState('')

  /** The exact frozen values the preview used — the live run reuses these verbatim. */
  const [sent, setSent] = useState<{ from: string; to: string; hard: boolean } | null>(null)
  const [previewedAt, setPreviewedAt] = useState<number | null>(null)
  const [clamped, setClamped] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  const resetPreview = () => {
    setPreview(null)
    setSent(null)
    setPreviewedAt(null)
    setConfirm('')
    setClamped(false)
  }

  const runPreview = async () => {
    if (!range) {
      message.warning('Pick a date range first')
      return
    }
    const at = new Date()
    const from = range[0].toISOString()
    const selectedTo = range[1].toDate()
    const effectiveTo = freezeUpperBound(selectedTo, at)
    const to = effectiveTo.toISOString()

    setBusy(true)
    try {
      const { data, error } = await supabaseClient.functions.invoke<PurgeResult>('feed-purge', {
        body: { from, to, dry_run: true, hard_delete: hardDelete, max },
      })
      if (error) throw error
      setPreview(data ?? null)
      setSent({ from, to, hard: hardDelete })
      setPreviewedAt(at.getTime())
      setClamped(effectiveTo.getTime() !== selectedTo.getTime())
      setConfirm('')
      setNow(Date.now())
      message.info(`${data?.matched ?? 0} item(s) match this window`)
    } catch (e) {
      message.error(await edgeErrorMessage(e, 'Preview failed'))
    } finally {
      setBusy(false)
    }
  }

  const runLive = async () => {
    // Reuse the FROZEN window, never the picker — recomputing here would
    // reintroduce the widening window this whole design exists to prevent.
    if (!sent || !preview) return
    setBusy(true)
    try {
      const { data, error } = await supabaseClient.functions.invoke<PurgeResult>('feed-purge', {
        body: {
          from: sent.from,
          to: sent.to,
          dry_run: false,
          hard_delete: sent.hard,
          max,
          confirm: confirmPhraseFor(sent.from, sent.to, sent.hard),
          reason: 'Bulk purge from the admin dashboard',
        },
      })
      if (error) throw error

      // A PARTIAL FAILURE ARRIVES AS HTTP 200 with { ok:false, failures:[…] },
      // and a short `deleted` can happen even with ok:true (Stream returning
      // fewer deleted_ids than requested). Treating any 2xx as success would
      // show a green "Purged 0 item(s)" over a run that deleted nothing — so
      // branch on the payload, and keep the form so the remainder can be redone.
      const deleted = data?.deleted ?? 0
      const complete = data?.ok !== false && deleted === preview.matched
      if (!complete) {
        const failed = data?.failures?.length ?? 0
        message.error(
          `Purged ${deleted} of ${preview.matched} — ${
            failed ? `${failed} batch(es) failed` : 'the server reported fewer deletions than matched'
          }. The window is kept so you can re-run it for the remainder.`,
        )
        onPurged?.()
        return
      }

      message.success(`Purged ${deleted} item(s) from the feed`)
      resetPreview()
      setRange(null)
      onPurged?.()
    } catch (e) {
      // A refused live run (cap, confirm mismatch) must invalidate the preview:
      // the count shown is one the server just declined to act on.
      message.error(await edgeErrorMessage(e, 'Purge failed'))
      resetPreview()
    } finally {
      setBusy(false)
    }
  }

  const expected = sent ? confirmPhraseFor(sent.from, sent.to, sent.hard) : ''
  const stale = previewedAt !== null && now - previewedAt > PREVIEW_STALE_MS
  const canPurge = !!preview && preview.matched > 0 && confirm === expected && !stale && !busy

  return (
    <SectionCard title="Bulk purge by date range">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type={hardDelete ? 'error' : 'warning'}
          showIcon
          message={
            hardDelete
              ? 'Permanent — these posts cannot be restored'
              : 'Removes every matching post in the window'
          }
          description={
            hardDelete
              ? 'Hard delete removes the activities from GetStream outright. There is no restore, and no record of which posts were removed — only how many.'
              : 'A soft purge hides the posts. Restoring is one-at-a-time from the list above, and only for items recent enough to appear there — that list shows the most recent activities, so an OLDER window may leave nothing visible to restore. Treat a soft purge of an old window as effectively final too.'
          }
        />

        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Range">
            <DatePicker.RangePicker
              showTime
              value={range}
              onChange={(v) => {
                setRange(v as [Dayjs, Dayjs] | null)
                resetPreview()
              }}
              style={{ width: '100%' }}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Permanent">
            <Space>
              <Switch checked={hardDelete} onChange={(v) => { setHardDelete(v); resetPreview() }} />
              <Typography.Text type={hardDelete ? 'danger' : undefined}>
                {hardDelete ? 'Hard delete — cannot be undone' : 'Soft delete — hidden, sometimes restorable'}
              </Typography.Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Safety cap">
            <Space>
              <InputNumber
                min={1}
                max={5000}
                value={max}
                onChange={(v) => { setMax(v ?? 500); resetPreview() }}
              />
              <Typography.Text type="secondary">
                Refuses outright above this many matches rather than half-deleting.
              </Typography.Text>
            </Space>
          </Descriptions.Item>
        </Descriptions>

        <Space>
          <Button onClick={() => void runPreview()} loading={busy} disabled={!range}>
            Preview (dry run)
          </Button>
        </Space>

        {preview && (
          <>
            {clamped && sent && (
              <Alert
                type="info"
                showIcon
                message="The end of the window was pulled back to the moment you previewed"
                description={`Purging up to "now" would also sweep anything posted between the preview and your confirmation. The window is fixed at ${new Date(sent.to).toLocaleString()}; anything posted after that is safe.`}
              />
            )}

            {stale && (
              <Alert
                type="warning"
                showIcon
                message="This preview is out of date — run it again"
                description="The purge re-queries the feed rather than deleting the exact items listed here, so a preview this old is no longer evidence of what would be removed."
              />
            )}

            <Alert
              type={preview.matched > 0 ? 'info' : 'success'}
              showIcon
              message={
                preview.matched > 0
                  ? `${preview.matched} item(s) would be ${preview.hard_delete ? 'permanently deleted' : 'removed'}`
                  : 'Nothing matches this window — nothing to do'
              }
              description={preview.note}
            />

            {preview.sample && preview.sample.length > 0 && (
              <>
                <Typography.Text type="secondary">
                  {preview.matched > SAMPLE_CAP
                    ? `Sample only — showing ${preview.sample.length} of ${preview.matched}. The rest are not listed, so confirm the WINDOW, not this list.`
                    : `All ${preview.matched} matching item(s):`}
                </Typography.Text>
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={preview.sample}
                  columns={[
                    { title: 'Post', dataIndex: 'text', ellipsis: true },
                    {
                      title: 'Posted',
                      dataIndex: 'created_at',
                      width: 190,
                      render: (v: string | null) => (v ? new Date(v).toLocaleString() : '—'),
                    },
                    { title: 'ID', dataIndex: 'id', width: 260, render: (v: string) => <Tag>{v}</Tag> },
                  ]}
                />
              </>
            )}

            {preview.matched > 0 && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Text>
                  Type <Typography.Text code copyable>{expected}</Typography.Text> to unlock the purge.
                </Typography.Text>
                <Input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onFocus={() => setNow(Date.now())}
                  placeholder={expected}
                  aria-label="Purge window confirmation"
                  style={{ maxWidth: 620 }}
                />
                <Button
                  danger
                  type="primary"
                  disabled={!canPurge}
                  loading={busy}
                  onClick={() => {
                    // Re-check staleness at click time, not only at render.
                    if (previewedAt !== null && Date.now() - previewedAt > PREVIEW_STALE_MS) {
                      setNow(Date.now())
                      message.warning('That preview has expired — run it again before purging')
                      return
                    }
                    void runLive()
                  }}
                >
                  {hardDelete ? 'Permanently delete' : 'Remove'} {preview.matched} item(s)
                </Button>
              </Space>
            )}
          </>
        )}
      </Space>
    </SectionCard>
  )
}
